/**
 * Tactile UI Web Audio Haptics Engine
 * - Zero background music / ambient drone
 * - Pure procedural synthesis: 0 network requests, 0 asset downloads
 * - Clean, crisp mechanical keyboard switch clicks for interactions
 * - Realistic typewriter/keyboard typing haptics for inputs
 * - Two-tone confirmation haptic for form submission
 * - Instant AudioContext unlock with pending strike replay
 * - On by default with local persistence
 */

let audioCtx = null;
let isUnlocked = false;
let pendingThunder = false;

// Audio haptics enabled by default
let soundEnabled = (() => {
  try {
    const saved = localStorage.getItem("sound");
    return saved === null ? true : saved === "true";
  } catch {
    return true;
  }
})();

const listeners = new Set();

export function getAudioContext() {
  if (!audioCtx && typeof window !== "undefined") {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      audioCtx = new AudioContext();
    }
  }
  return audioCtx;
}

export function isAudioUnlocked() {
  return isUnlocked || (audioCtx && audioCtx.state === "running");
}

export function unlockAudio() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    ctx
      .resume()
      .then(() => {
        isUnlocked = true;
        if (pendingThunder && soundEnabled) {
          pendingThunder = false;
          playThunder();
        }
      })
      .catch(() => {});
  } else if (ctx.state === "running") {
    isUnlocked = true;
    if (pendingThunder && soundEnabled) {
      pendingThunder = false;
      playThunder();
    }
  }

  // iOS Safari / WebKit silent buffer unlock
  try {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (e) {}
}

export function initAudio() {
  if (soundEnabled) {
    unlockAudio();
  }
}

export function isSoundEnabled() {
  return soundEnabled;
}

export function toggleSound() {
  soundEnabled = !soundEnabled;
  try {
    localStorage.setItem("sound", String(soundEnabled));
  } catch {}

  if (soundEnabled) {
    unlockAudio();
    playClick();
  }

  listeners.forEach((fn) => fn(soundEnabled));
  return soundEnabled;
}

export function subscribeSoundState(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Atmospheric Thunder Rumble (Available on first strike)
 */
export function playThunder() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const runSynth = () => {
    try {
      const duration = 2.4;
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      // Generate brown/pink filtered noise
      let lastOut = 0.0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = data[i];
        data[i] *= 3.5; // Gain boost
      }

      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      // Low-pass resonant filter for thunderous low-end
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(140, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(
        45,
        ctx.currentTime + duration
      );
      filter.Q.setValueAtTime(4, ctx.currentTime);

      // Gain envelope
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      noise.start();
      noise.stop(ctx.currentTime + duration);
      pendingThunder = false;
    } catch (e) {}
  };

  if (ctx.state === "suspended") {
    pendingThunder = true;
    ctx
      .resume()
      .then(() => {
        isUnlocked = true;
        runSynth();
      })
      .catch(() => {
        pendingThunder = true;
      });
  } else {
    runSynth();
  }
}

/**
 * Tactile Mechanical Switch Click (Crisp & Tactile)
 */
export function playClick() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const runClick = () => {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(1700, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.032);

      gain.gain.setValueAtTime(0.09, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.032);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.032);
    } catch (e) {}
  };

  if (ctx.state === "suspended") {
    ctx.resume().then(runClick).catch(runClick);
  } else {
    runClick();
  }
}

/**
 * Realistic Typing Keypress Sound (Slightly pitched variation for organic tactile feel)
 */
export function playKeyClick() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Random pitch variance for mechanical key feel (1300Hz - 1900Hz)
    const baseFreq = 1400 + Math.random() * 500;
    osc.type = Math.random() > 0.5 ? "sine" : "triangle";
    osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.024);

    gain.gain.setValueAtTime(0.045, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.024);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.024);
  } catch (e) {}
}

/**
 * Form Submit / Action Success Sound (Clean two-tone haptic)
 */
export function playSubmit() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;

    // Tone 1
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, now); // D5
    gain1.gain.setValueAtTime(0.07, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.12);

    // Tone 2 (Higher rising note)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880.0, now + 0.08); // A5
    gain2.gain.setValueAtTime(0.001, now);
    gain2.gain.setValueAtTime(0.08, now + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.32);
  } catch (e) {}
}

/**
 * Subtle Micro-Blip on Hover
 */
export function playHover() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(750, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.022);

    gain.gain.setValueAtTime(0.018, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.022);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.022);
  } catch (e) {}
}

/**
 * Electric Snap / Zap for divider or interaction
 */
export function playZap() {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(2800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(280, ctx.currentTime + 0.05);

    gain.gain.setValueAtTime(0.04, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.05);
  } catch (e) {}
}

let lastBeamSoundTime = 0;

/**
 * Magnetic dial ratchet / tactile sub-pulse for the Experience timeline scroll beam
 * @param {'down' | 'up'} direction - Scroll direction
 * @param {number} progress - Timeline progress (0.0 to 1.0)
 */
export function playBeamSound(direction = "down", progress = 0.5) {
  if (!soundEnabled) return;
  const now = performance.now();
  if (now - lastBeamSoundTime < 85) return;
  lastBeamSoundTime = now;

  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const duration = 0.032;

    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    // Deep, warm magnetic frequency range (190Hz - 285Hz)
    const baseFreq =
      direction === "down"
        ? 190 + clampedProgress * 95
        : 285 - (1 - clampedProgress) * 95;

    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = "triangle";
    osc.frequency.setValueAtTime(baseFreq, audioNow);
    osc.frequency.exponentialRampToValueAtTime(
      direction === "down" ? baseFreq * 1.15 : baseFreq * 0.88,
      audioNow + duration
    );

    // Warm resonant lowpass filter for punchy magnetic thud
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(520, audioNow);
    filter.Q.setValueAtTime(2.5, audioNow);

    gain.gain.setValueAtTime(0.026, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + duration);
  } catch (e) {}
}

let lastCardHoverTime = 0;

/**
 * Velvety glass resonance for card hover
 */
export function playCardHover() {
  if (!soundEnabled) return;
  const now = performance.now();
  if (now - lastCardHoverTime < 110) return;
  lastCardHoverTime = now;

  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const audioNow = ctx.currentTime;
    const duration = 0.026;

    osc.type = "sine";
    osc.frequency.setValueAtTime(540, audioNow);
    osc.frequency.exponentialRampToValueAtTime(720, audioNow + duration);

    gain.gain.setValueAtTime(0.015, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + duration);
  } catch (e) {}
}

let lastLaserSweepTime = 0;

/**
 * Subtle ion shimmer for section divider laser crossing
 */
export function playLaserSweep() {
  if (!soundEnabled) return;
  const now = performance.now();
  if (now - lastLaserSweepTime < 800) return;
  lastLaserSweepTime = now;

  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const audioNow = ctx.currentTime;
    const duration = 0.045;

    osc.type = "sine";
    osc.frequency.setValueAtTime(1200, audioNow);
    osc.frequency.exponentialRampToValueAtTime(400, audioNow + duration);

    gain.gain.setValueAtTime(0.014, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + duration);
  } catch (e) {}
}

let lastEngineRevTime = 0;

/**
 * Cyberpunk Turbo RPM Engine Rev SFX (Procedural dual-oscillator synthesis)
 */
export function playEngineRev() {
  if (!soundEnabled) return;
  const now = performance.now();
  if (now - lastEngineRevTime < 450) return;
  lastEngineRevTime = now;

  const ctx = getAudioContext();
  if (!ctx) return;

  const runSynth = () => {
    try {
      const audioNow = ctx.currentTime;
      const duration = 0.65;

      // 1. Primary Engine Oscillator (Sawtooth with RPM pitch throttle curve)
      const osc1 = ctx.createOscillator();
      osc1.type = "sawtooth";
      osc1.frequency.setValueAtTime(55, audioNow);
      osc1.frequency.exponentialRampToValueAtTime(320, audioNow + 0.28);
      osc1.frequency.exponentialRampToValueAtTime(140, audioNow + duration);

      // 2. Sub Harmonic Bass Oscillator
      const osc2 = ctx.createOscillator();
      osc2.type = "triangle";
      osc2.frequency.setValueAtTime(45, audioNow);
      osc2.frequency.exponentialRampToValueAtTime(180, audioNow + 0.28);
      osc2.frequency.exponentialRampToValueAtTime(80, audioNow + duration);

      // 3. Resonant Lowpass Filter (Simulating exhaust resonance & engine chamber)
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(220, audioNow);
      filter.frequency.exponentialRampToValueAtTime(1400, audioNow + 0.28);
      filter.frequency.exponentialRampToValueAtTime(300, audioNow + duration);
      filter.Q.setValueAtTime(4.5, audioNow);

      // 4. Main Gain Envelope
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, audioNow);
      gain.gain.linearRampToValueAtTime(0.09, audioNow + 0.08);
      gain.gain.linearRampToValueAtTime(0.075, audioNow + 0.32);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(audioNow);
      osc2.start(audioNow);
      osc1.stop(audioNow + duration);
      osc2.stop(audioNow + duration);
    } catch (e) {}
  };

  if (ctx.state === "suspended") {
    ctx.resume().then(() => {
      isUnlocked = true;
      runSynth();
    });
  } else {
    runSynth();
  }
}

/**
 * Bookshelf Haptic: Tactile Wooden Pull & Select Thud
 */
export function playBookSelect(volume = 1) {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(320, audioNow);
    osc.frequency.exponentialRampToValueAtTime(75, audioNow + 0.045);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(450, audioNow);

    gain.gain.setValueAtTime(0.06 * volume, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + 0.045);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + 0.045);
  } catch (e) {}
}

/**
 * Bookshelf Haptic: Subtle Carousel Sliding Notch Click
 */
export function playBookNavigate(volume = 1) {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "triangle";
    osc.frequency.setValueAtTime(680, audioNow);
    osc.frequency.exponentialRampToValueAtTime(240, audioNow + 0.02);

    gain.gain.setValueAtTime(0.03 * volume, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + 0.02);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + 0.02);
  } catch (e) {}
}

/**
 * Bookshelf Haptic: Rich Cloth Spine & Cover Opening Whoosh
 */
export function playBookOpen(volume = 1) {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const duration = 0.22;

    // Resonant cloth hinge friction
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(140, audioNow);
    osc.frequency.exponentialRampToValueAtTime(420, audioNow + duration * 0.5);
    osc.frequency.exponentialRampToValueAtTime(110, audioNow + duration);

    filter.type = "bandpass";
    filter.frequency.setValueAtTime(380, audioNow);
    filter.frequency.linearRampToValueAtTime(900, audioNow + duration * 0.4);
    filter.Q.setValueAtTime(2.5, audioNow);

    gain.gain.setValueAtTime(0.001, audioNow);
    gain.gain.linearRampToValueAtTime(0.045 * volume, audioNow + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + duration);
  } catch (e) {}
}

/**
 * Bookshelf Haptic: Organic Paper Leaf Flip Rustle
 */
export function playPageTurn(volume = 1) {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const duration = 0.085;

    // Filtered noise pulse simulating paper fiber rustle
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.sin((i / bufferSize) * Math.PI);
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(1800, audioNow);
    filter.frequency.exponentialRampToValueAtTime(800, audioNow + duration);
    filter.Q.setValueAtTime(1.8, audioNow);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.035 * volume, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(audioNow);
    noise.stop(audioNow + duration);
  } catch (e) {}
}

/**
 * Bookshelf Haptic: Book Closure Thump & Snap
 */
export function playBookClose(volume = 1) {
  if (!soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const audioNow = ctx.currentTime;
    const duration = 0.12;

    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(260, audioNow);
    osc.frequency.exponentialRampToValueAtTime(55, audioNow + duration);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(420, audioNow);

    gain.gain.setValueAtTime(0.07 * volume, audioNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioNow + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(audioNow);
    osc.stop(audioNow + duration);
  } catch (e) {}
}

// Proactive global gesture unlock for instant first-strike audio
if (typeof window !== "undefined") {
  const unlockEvents = [
    "pointerdown",
    "pointerup",
    "touchstart",
    "touchend",
    "mousedown",
    "mouseup",
    "keydown",
    "click",
    "wheel",
  ];

  const handleGlobalUnlock = () => {
    unlockAudio();
    const ctx = getAudioContext();
    if (ctx && ctx.state === "running") {
      isUnlocked = true;
      unlockEvents.forEach((evt) => {
        window.removeEventListener(evt, handleGlobalUnlock, true);
        document.removeEventListener(evt, handleGlobalUnlock, true);
      });
    }
  };

  unlockEvents.forEach((evt) => {
    window.addEventListener(evt, handleGlobalUnlock, {
      passive: true,
      capture: true,
    });
    document.addEventListener(evt, handleGlobalUnlock, {
      passive: true,
      capture: true,
    });
  });
}
